import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import { Colors } from "../../../../utils/colors";
import CreatorModePanelWrapper from "../../../../components/CreatorDashboard/CreatorModePanelWrapper";
import StandardBorderedContainer from "../../../../components/Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../../../components/Dividers/HorizontalDivider";
import { TabPanel } from "../../../../components/FullEventSubComponents/TabPanel";
import {
  getAccountSlice,
  getIncompleteOnboardingFlows,
} from "../../../../redux/slices/accountSlice";
import { hasSocialAccountConnection } from "../../../../utils/socialAccountUtils";
import {
  DMAutomationPlatform,
  DMAutomationTrigger,
  OnboardingFlowType,
} from "@markit/common.types";
import AutoDMAutomationsPanel from "../../../../components/CreatorDashboard/AutoDM/AutoDMAutomationsPanel";
import CreateDMAutomationWrapper from "../../../../components/CreatorDashboard/AutoDM/Dashboard/CreateDMAutomationWrapper";
import { isReplyTrigger, MARKIT_DOCS } from "@markit/common.utils";
import SelectAutomationTriggerModal from "../../../../components/CreatorDashboard/AutoDM/Dashboard/SelectAutomationTriggerModal";
import { InstagramPanelSection } from "../../../../components/CreatorDashboard/AutoDM/Instagram/InstagramPanelSection";
import { FacebookPanelSection } from "../../../../components/CreatorDashboard/AutoDM/Facebook/FacebookPanelSection";
import OnboardingFlowDot from "../../../../components/Onboarding/OnboardingFlowDot";
import RectangleButtonCreatorPanel from "../../../../components/Buttons/RectangleButtonCreatorPanel";

export enum AutoDMTabs {
  AUTOMATIONS = 0,
  CONNECTION = 1,
}

const AutoDMPanel = () => {
  const { instagramAccounts, facebookPages } = useSelector(getAccountSlice);
  const incompleteOnboardingFlows = useSelector(getIncompleteOnboardingFlows);
  const [selectTriggerModal, setSelectTriggerModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newAutomationPlatform, setNewAutomationPlatform] =
    useState<DMAutomationPlatform>(DMAutomationPlatform.INSTAGRAM);
  const [newAutomationTrigger, setNewAutomationTrigger] = useState<
    DMAutomationTrigger | undefined
  >(undefined);

  const hasAnyConnection = useMemo(
    () =>
      hasSocialAccountConnection(instagramAccounts) ||
      hasSocialAccountConnection(facebookPages),
    [instagramAccounts, facebookPages]
  );
  const [tabValue, setTabValue] = useState(() =>
    incompleteOnboardingFlows.some(
      (flow) => flow.id === OnboardingFlowType.INSTAGRAM_DM
    ) && !hasAnyConnection
      ? AutoDMTabs.CONNECTION
      : AutoDMTabs.AUTOMATIONS
  );

  const styles = {
    overallContainerView: {
      backgroundColor: Colors.WHITE,
      flex: 1,
      width: "100%",
      minHeight: 237,
      borderRadius: 20,
      paddingBlock: 14,
    },
  };

  const descriptionItems = useMemo(
    () => [
      {
        icon: "fa-solid:plug",
        text: "Auto DM allows you to automatically send social media direct messages when someone comments on your post or reel.",
      },
      {
        icon: "ion:sync-sharp",
        text: "Once connected, you can trigger personalized DM sequences, send links, and convert your social media followers into SMS subscribers automatically.",
      },
      {
        icon: "ion:close-circle",
        text: "Pause or turn off your Auto DM automations at any time.",
      },
    ],
    []
  );

  return (
    <CreatorModePanelWrapper
      title="Auto DM"
      subtext="Start converting your followers into SMS subscribers via Instagram messages."
      headerTabs={{
        tabNames: ["Automations", "Connection"],
        tabValue,
        setTabValue,
      }}
      headerRight={
        <div className="AlignedRow" style={{ gap: 14 }}>
          {hasAnyConnection ? (
            <RectangleButtonCreatorPanel
              title="New Automation"
              iconName="ion:add-circle"
              rightIcon={
                <OnboardingFlowDot
                  flowTypes={[OnboardingFlowType.INSTAGRAM_DM]}
                />
              }
              onPress={() => setSelectTriggerModal(true)}
            />
          ) : null}
        </div>
      }
    >
      <div
        style={{
          padding: 30,
          height: "100%",
          backgroundColor:
            tabValue === AutoDMTabs.AUTOMATIONS ? Colors.WHITE : Colors.WHITE1,
        }}
      >
        <AutoDMAutomationsPanel
          tabValue={tabValue}
          setTabValue={setTabValue}
          setSelectTriggerModal={setSelectTriggerModal}
        />
        <TabPanel value={tabValue} index={AutoDMTabs.CONNECTION}>
          <div className="RowNormal" style={{ gap: 20, alignItems: "stretch" }}>
            <InstagramPanelSection />
            <FacebookPanelSection />
            <StandardBorderedContainer
              containerStyles={styles.overallContainerView}
            >
              <div className="ColumnNormal">
                <div className="AlignedRowSpaced" style={{ paddingInline: 14 }}>
                  <span className="bodyMedium">About Auto DM</span>
                  <div
                    className="AlignedRowSelect"
                    style={{ gap: 2 }}
                    onClick={() =>
                      window.open(MARKIT_DOCS + "/autodmoverview", "_blank")
                    }
                  >
                    <span className="bodySmallMedium">Learn More</span>
                    <Icon
                      icon="ion:arrow-forward"
                      height={15}
                      style={{
                        transform: "rotateY(0deg) rotate(-45deg)",
                        marginBottom: -1,
                      }}
                    />
                  </div>
                </div>
                <HorizontalDivider altMargin={14} />
                <div
                  className="ColumnNormal"
                  style={{ gap: 14, paddingInline: 14 }}
                >
                  <div
                    className="AlignedRow"
                    style={{ gap: 14, paddingRight: 14 }}
                  >
                    <div>
                      <Icon icon="fa-solid:plug" height={25} width={25} />
                    </div>
                    <span className="bodyRegular">
                      {descriptionItems[0].text}
                    </span>
                  </div>
                  <div
                    className="AlignedRow"
                    style={{ gap: 14, paddingRight: 14 }}
                  >
                    <div>
                      <Icon icon="ion:sync-sharp" height={25} width={25} />
                    </div>
                    <span className="bodyRegular">
                      {descriptionItems[1].text}
                    </span>
                  </div>
                  <div
                    className="AlignedRow"
                    style={{ gap: 14, paddingRight: 14 }}
                  >
                    <div>
                      <Icon icon="ion:close-circle" height={25} width={25} />
                    </div>
                    <span className="bodyRegular">
                      {descriptionItems[2].text}
                    </span>
                  </div>
                </div>
              </div>
            </StandardBorderedContainer>
          </div>
        </TabPanel>
        {selectTriggerModal ? (
          <SelectAutomationTriggerModal
            showModal={selectTriggerModal}
            setShowModal={setSelectTriggerModal}
            onSelectTrigger={(trigger, platform) => {
              setNewAutomationPlatform(platform);
              // Only reply triggers carry over; comment triggers keep the wrapper's existing default
              setNewAutomationTrigger(
                isReplyTrigger(trigger) ? trigger : undefined
              );
              setSelectTriggerModal(false);
              setShowCreateModal(true);
            }}
          />
        ) : null}
        {showCreateModal ? (
          <CreateDMAutomationWrapper
            showModal={showCreateModal}
            setShowModal={setShowCreateModal}
            initialPlatform={newAutomationPlatform}
            initialTrigger={newAutomationTrigger}
          />
        ) : null}
      </div>
    </CreatorModePanelWrapper>
  );
};

export default AutoDMPanel;
